import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { X, PlayCircle } from "lucide-react";
import { PERSONAL_ASSISTANT_NAME } from "../../constants";

interface ExecutiveAssistantTutorialDialogProps {
  open: boolean;
  onClose: () => void;
}

// Executive Assistant training video from the training page
const EXECUTIVE_ASSISTANT_VIDEO_ID = "1zPfLBEeb2WBn58mrcWG1E0i4amztr_DY";

const ExecutiveAssistantTutorialDialog: React.FC<ExecutiveAssistantTutorialDialogProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getDriveEmbedUrl = (fileId: string) => {
    return `https://drive.google.com/file/d/${fileId}/preview`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PlayCircle size={24} />
          <Typography variant="h6" component="span">
            {`Getting Started with Your ${PERSONAL_ASSISTANT_NAME}`}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: theme.palette.text.secondary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {`Your ${PERSONAL_ASSISTANT_NAME} is the fastest way to get things done. Watch this quick tutorial
          to learn how to make the most of your AI-powered assistant and supercharge your productivity.`}
        </Typography>

        {/* Video Embed */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            paddingTop: "56.25%", // 16:9 aspect ratio
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: "action.hover",
          }}
        >
          <iframe
            src={getDriveEmbedUrl(EXECUTIVE_ASSISTANT_VIDEO_ID)}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="Executive Assistant Tutorial"
          />
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 1,
            bgcolor: "action.hover",
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            What you can do:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Ask questions and get instant answers
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Manage your tasks and schedule
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Upload documents and images for analysis
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Connect integrations to extend capabilities
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Get Started
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExecutiveAssistantTutorialDialog;
